// src/pages/DashboardPage.js
import { useEffect, useState } from 'react';
import { getProducts, getSales, getStores } from '../api/apiService';

const bs = { fontFamily: "'Montserrat', sans-serif" };
const ds = { fontFamily: "'Cormorant Garamond', Georgia, serif" };

const inputStyle = {
  ...bs, width: '100%', padding: '9px 12px',
  border: '1px solid #e5e0e0', fontSize: '12px', color: '#060D14',
  outline: 'none', borderRadius: '2px', background: 'white',
  transition: 'border-color 0.2s',
};

const StatCard = ({ label, value, accent, prefix = '', suffix = '' }) => (
  <div style={{
    background: 'white', border: '1px solid #f0eaea',
    borderTop: `3px solid ${accent}`,
    padding: '24px 20px', borderRadius: '2px',
    transition: 'box-shadow 0.2s',
  }}
  onMouseOver={e => e.currentTarget.style.boxShadow = '0 8px 32px rgba(0,0,0,0.06)'}
  onMouseOut={e => e.currentTarget.style.boxShadow = 'none'}>
    <p style={{ ...bs, fontSize: '10px', fontWeight: 600, letterSpacing: '0.2em',
      textTransform: 'uppercase', color: '#bbb', marginBottom: '12px' }}>
      {label}
    </p>
    <p style={{ ...ds, fontSize: '2.2rem', fontWeight: 400, color: '#060D14',
      lineHeight: 1, margin: 0 }}>
      {prefix}{typeof value === 'number' && !Number.isInteger(value)
        ? value.toFixed(2) : value}{suffix}
    </p>
  </div>
);

const DashboardPage = () => {
  const [stats, setStats] = useState({
    totalProducts: 0, totalStock: 0, totalSalesCount: 0,
    totalRevenue: 0, totalInventoryValue: 0, totalGrossProfit: 0,
    lowStockItems: [], totalStores: 0, salesByStore: {}
  });
  const [stores,          setStores]          = useState([]);
  const [selectedStoreId, setSelectedStoreId] = useState('');
  const [startDate,       setStartDate]       = useState('');
  const [endDate,         setEndDate]         = useState('');
  const [loading,         setLoading]         = useState(true);

  useEffect(() => {
    (async () => {
      const loadedStores = await getStores();
      setStores(loadedStores);
      const today = new Date();
      const ago30 = new Date(); ago30.setDate(today.getDate() - 30);
      setEndDate(today.toISOString().split('T')[0]);
      setStartDate(ago30.toISOString().split('T')[0]);
    })();
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [products, sales, loadedStores] = await Promise.all([
          getProducts(), getSales(), getStores()
        ]);

        let filteredProducts = products;
        let filteredSales    = sales;

        if (selectedStoreId) {
          filteredProducts = products.filter(p =>
            p.assignedStores?.includes(selectedStoreId)
          );
          filteredSales = sales.filter(s => String(s.storeId) === selectedStoreId);
        }

        if (startDate && endDate) {
          const start = new Date(startDate).setHours(0,0,0,0);
          const end   = new Date(endDate).setHours(23,59,59,999);
          filteredSales = filteredSales.filter(s => {
            const d = new Date(s.date).getTime();
            return d >= start && d <= end;
          });
        }

        let totalStock = 0, totalInventoryValue = 0, totalGrossProfit = 0;
        filteredProducts.forEach(p => {
          p.variants.forEach(v => {
            const stocks = v.storeStocks || {};
            const variantStock = selectedStoreId
              ? (stocks[selectedStoreId] || 0)
              : Object.values(stocks).reduce((a, b) => a + b, 0);
            totalStock         += variantStock;
            totalInventoryValue += variantStock * (p.productionCost || 0);
          });
        });

        const totalRevenue = filteredSales.reduce((s, sale) => s + sale.total, 0);
        filteredSales.forEach(sale => {
          (sale.items || []).forEach(item => {
            const prod = products.find(p => p.id === item.productId);
            if (prod) totalGrossProfit += (item.price - (prod.productionCost || 0)) * item.quantity;
          });
        });

        const lowStockItems = filteredProducts.flatMap(p =>
          p.variants
            .filter(v => {
              const stocks    = v.storeStocks || {};
              const threshold = p.lowStockThreshold ?? 5;
              return selectedStoreId
                ? (stocks[selectedStoreId] || 0) <= threshold
                : Object.values(stocks).some(s => s <= threshold);
            })
            .map(v => ({
              product: p.name,
              variant: `${v.size} / ${v.color}`,
              stock: selectedStoreId
                ? (v.storeStocks?.[selectedStoreId] || 0)
                : Object.entries(v.storeStocks || {})
                    .map(([sid, s]) => `${loadedStores.find(st => String(st.id) === sid)?.name || sid}: ${s}`)
                    .join(', ')
            }))
        );

        const salesByStore = {};
        loadedStores.forEach(store => {
          const storeSales = filteredSales.filter(s => String(s.storeId) === String(store.id));
          salesByStore[store.name] = storeSales.reduce((sum, s) => sum + s.total, 0);
        });

        setStats({
          totalProducts: filteredProducts.length, totalStock,
          totalSalesCount: filteredSales.length, totalRevenue,
          totalInventoryValue, totalGrossProfit, lowStockItems,
          totalStores: loadedStores.length, salesByStore
        });
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    })();
  }, [selectedStoreId, startDate, endDate, stores]);

  const maxRevenue = Math.max(...Object.values(stats.salesByStore), 0);

  return (
    <div style={{ padding: '24px 16px', maxWidth: '1200px', margin: '0 auto' }}>

      {/* Header */}
      <div style={{ marginBottom: '28px' }}>
        <h1 style={{ ...ds, fontSize: '2rem', fontWeight: 400, color: '#060D14', margin: '0 0 4px' }}>
          Dashboard
        </h1>
        <p style={{ ...bs, fontSize: '12px', color: '#bbb', margin: 0 }}>
          Resumen de rendimiento · últimos 30 días
        </p>
      </div>

      {/* Filtros */}
      <div style={{ background: 'white', border: '1px solid #f0eaea',
        padding: '20px 24px', borderRadius: '2px', marginBottom: '24px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px,1fr))',
          gap: '16px', alignItems: 'end' }}>
          <div>
            <label style={{ ...bs, display: 'block', fontSize: '10px', letterSpacing: '0.18em',
              textTransform: 'uppercase', color: '#999', marginBottom: '6px' }}>
              Tienda
            </label>
            <select value={selectedStoreId} onChange={e => setSelectedStoreId(e.target.value)}
              style={inputStyle}
              onFocus={e => e.target.style.borderColor = '#961914'}
              onBlur={e => e.target.style.borderColor = '#e5e0e0'}>
              <option value="">Todas las tiendas</option>
              {stores.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>
          <div>
            <label style={{ ...bs, display: 'block', fontSize: '10px', letterSpacing: '0.18em',
              textTransform: 'uppercase', color: '#999', marginBottom: '6px' }}>
              Fecha inicio
            </label>
            <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)}
              style={inputStyle}
              onFocus={e => e.target.style.borderColor = '#961914'}
              onBlur={e => e.target.style.borderColor = '#e5e0e0'} />
          </div>
          <div>
            <label style={{ ...bs, display: 'block', fontSize: '10px', letterSpacing: '0.18em',
              textTransform: 'uppercase', color: '#999', marginBottom: '6px' }}>
              Fecha fin
            </label>
            <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)}
              style={inputStyle}
              onFocus={e => e.target.style.borderColor = '#961914'}
              onBlur={e => e.target.style.borderColor = '#e5e0e0'} />
          </div>
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '48px' }}>
          <p style={{ ...bs, fontSize: '12px', color: '#bbb', letterSpacing: '0.15em',
            textTransform: 'uppercase' }}>Calculando estadísticas…</p>
        </div>
      ) : (
        <>
          {/* KPI cards */}
          <div style={{ display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(200px,1fr))',
            gap: '12px', marginBottom: '24px' }}>
            <StatCard label="Productos"       value={stats.totalProducts}       accent="#961914" />
            <StatCard label="Stock Total"     value={stats.totalStock}          accent="#3b82f6" />
            <StatCard label="Ventas"          value={stats.totalSalesCount}     accent="#22c55e" />
            <StatCard label="Ingresos"        value={stats.totalRevenue}        accent="#f59e0b" prefix="$" />
            <StatCard label="Valor Inventario" value={stats.totalInventoryValue} accent="#f97316" prefix="$" />
            <StatCard label="Utilidad Bruta"  value={stats.totalGrossProfit}    accent="#14b8a6" prefix="$" />
            {!selectedStoreId && (
              <StatCard label="Tiendas" value={stats.totalStores} accent="#8b5cf6" />
            )}
          </div>

          {/* Gráfico + Stock bajo */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr',
            gap: '16px' }} className="grid-cols-1 lg:grid-cols-2">

            {/* Ventas por tienda */}
            <div style={{ background: 'white', border: '1px solid #f0eaea',
              padding: '24px', borderRadius: '2px' }}>
              <h2 style={{ ...ds, fontSize: '1.3rem', fontWeight: 600,
                color: '#060D14', marginBottom: '20px' }}>
                Ventas por Tienda
              </h2>
              {maxRevenue === 0 ? (
                <p style={{ ...bs, textAlign: 'center', color: '#bbb',
                  fontSize: '12px', padding: '24px 0' }}>
                  Sin ventas en este período.
                </p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                  {Object.entries(stats.salesByStore).map(([name, revenue]) => (
                    <div key={name}>
                      <div style={{ display: 'flex', justifyContent: 'space-between',
                        marginBottom: '6px' }}>
                        <span style={{ ...bs, fontSize: '12px', color: '#060D14',
                          fontWeight: 500 }}>{name}</span>
                        <span style={{ ...ds, fontSize: '1rem', color: '#961914' }}>
                          ${revenue.toFixed(2)}
                        </span>
                      </div>
                      <div style={{ height: '4px', background: '#f5f0f0',
                        borderRadius: '2px', overflow: 'hidden' }}>
                        <div style={{
                          height: '100%', background: '#961914', borderRadius: '2px',
                          width: `${(revenue / maxRevenue) * 100}%`,
                          transition: 'width 0.8s ease',
                        }} />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Stock bajo */}
            <div style={{ background: 'white', border: '1px solid #f0eaea',
              padding: '24px', borderRadius: '2px' }}>
              <h2 style={{ ...ds, fontSize: '1.3rem', fontWeight: 600,
                color: '#060D14', marginBottom: '20px' }}>
                Stock Bajo
              </h2>
              {stats.lowStockItems.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '24px 0' }}>
                  <div style={{ fontSize: '28px', marginBottom: '8px' }}>✓</div>
                  <p style={{ ...bs, fontSize: '12px', color: '#22c55e', fontWeight: 600 }}>
                    Todo el inventario está bien
                  </p>
                </div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid #f0eaea' }}>
                        {['Producto', 'Variante', 'Stock'].map(h => (
                          <th key={h} style={{ ...bs, padding: '8px 10px', textAlign: 'left',
                            fontSize: '10px', fontWeight: 600, letterSpacing: '0.15em',
                            textTransform: 'uppercase', color: '#bbb' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {stats.lowStockItems.map((item, i) => (
                        <tr key={i} style={{ borderBottom: '1px solid #faf5f5' }}>
                          <td style={{ ...bs, padding: '10px', fontSize: '12px',
                            color: '#060D14', fontWeight: 500 }}>{item.product}</td>
                          <td style={{ ...bs, padding: '10px', fontSize: '11px',
                            color: '#aaa' }}>{item.variant}</td>
                          <td style={{ ...bs, padding: '10px', fontSize: '12px',
                            color: '#dc2626', fontWeight: 600 }}>{item.stock}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default DashboardPage;
